import React from 'react';
import { Input } from 'reactstrap';
import { toast } from 'react-toastify';
import EmployeeService from '../../../services/Employee';
import PerformanceFeedbackService from '../../../services/PerformanceFeedback';
import PolicyViolationsService from '../../../services/PolicyViolations';
import { FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import { createOptionsPage } from '../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../entities/PagedMeta';

export interface PerformanceFeedbackImportData {
  employeesList: any[];
  policyViolationsList: any[];
}

export const loadPerformanceFeedbackData = async (
  token: string,
  schoolId: string,
): Promise<PerformanceFeedbackImportData> => {
  const employeesList = await loadEmployees(token, schoolId);
  const policyViolationsList = await loadPolicyViolations(token);

  return {
    employeesList,
    policyViolationsList,
  };
};

const loadEmployees = async (token: string, schoolId: string): Promise<any[]> => {
  try {
    const filteredOptions: FilteredOptionsInterface = {
      filters: [],
      options: createOptionsPage(createPagedMeta()),
    };
    filteredOptions.options.itemsPerPage = 100000;
    const result = await EmployeeService(token).listEmployeesBySchool(schoolId, '', filteredOptions);
    const employeesWithUsers = (result.data || []).filter((emp: any) => emp.user);
    return employeesWithUsers || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No employees found');
    return [];
  }
};

const loadPolicyViolations = async (token: string): Promise<any[]> => {
  try {
    const result = await PolicyViolationsService(token).list();
    return result.data || [];
  } catch (e: any) {
    toast.error(e?.response?.data?.message || 'No policy violations found');
    return [];
  }
};

export interface ProcessedFeedback {
  userId: string;
  name: string;
  dateOfIncident: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  descriptionOfFeedback: string;
  immediateFeedback: string;
  expectationForImprovement: string;
  emailOfFeedback: string;
  approved: boolean;
}

export interface ProcessingErrors {
  [key: string]: string;
}

export const processPerformanceFeedbackImport = (
  importedLines: any[],
  formData: any[],
  employeesList: any[],
  policyViolationsList: any[],
): { feedbacks: ProcessedFeedback[]; errors: ProcessingErrors } => {
  const feedbacks: ProcessedFeedback[] = [];
  const errors: ProcessingErrors = {};

  for (let i = 0; i < importedLines.length; i++) {
    const line = importedLines[i];
    const formLine = formData[i] || {};

    const firstName = formLine['first_name'] || line['first_name'] || '';
    const lastName = formLine['last_name'] || line['last_name'] || '';
    const staffNameOrId =
      formLine['staff_name'] ||
      formLine['staffname'] ||
      line['staff_name'] ||
      line['staffname'] ||
      formLine['staff'] ||
      line['staff'] ||
      `${firstName} ${lastName}`.trim() ||
      '';
    const dateOfIncident =
      formLine['date_of_incident'] ||
      formLine['dateofincident'] ||
      line['date_of_incident'] ||
      line['dateofincident'] ||
      formLine['date'] ||
      line['date'] ||
      '';
    const policyViolationsStr =
      formLine['policy_violations'] ||
      formLine['policyviolations'] ||
      line['policy_violations'] ||
      line['policyviolations'] ||
      formLine['policy_violation'] ||
      line['policy_violation'] ||
      '';
    const descriptionOfFeedback =
      formLine['description_of_feedback'] ||
      formLine['descriptionoffeedback'] ||
      line['description_of_feedback'] ||
      line['descriptionoffeedback'] ||
      formLine['description'] ||
      line['description'] ||
      '';
    const immediateFeedback =
      formLine['immediate_feedback'] ||
      formLine['immediatefeedback'] ||
      line['immediate_feedback'] ||
      line['immediatefeedback'] ||
      formLine['immediate'] ||
      line['immediate'] ||
      '';
    const expectationForImprovement =
      formLine['expectations_for_improvement'] ||
      formLine['expectationsforimprovement'] ||
      line['expectations_for_improvement'] ||
      line['expectationsforimprovement'] ||
      formLine['expectation'] ||
      line['expectation'] ||
      '';

    let employee = employeesList.find((emp: any) => emp.user?.id === staffNameOrId);
    if (!employee) {
      employee = employeesList.find((emp: any) => {
        const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
        return fullName.toLowerCase() === String(staffNameOrId).toLowerCase().trim();
      });
    }

    if (!employee || !employee.user) {
      errors[`${i}.userId`] = `Staff member "${staffNameOrId}" not found`;
      continue;
    }

    const cleanedPolicyViolationsStr = String(policyViolationsStr || '').trim();
    const isNonePolicyViolation =
      cleanedPolicyViolationsStr.length === 0 ||
      cleanedPolicyViolationsStr.toLowerCase() === 'none' ||
      cleanedPolicyViolationsStr.toLowerCase().startsWith('none ');

    const policyViolationNames = isNonePolicyViolation
      ? []
      : cleanedPolicyViolationsStr
          .split(/[,;]/)
          .map((name: string) => name.trim())
          .filter((name: string) => name.length > 0);

    const policyViolationIds: string[] = [];
    const notFoundViolations: string[] = [];

    for (const pvName of policyViolationNames) {
      const pv = policyViolationsList.find((pv: any) => pv.name.toLowerCase() === pvName.toLowerCase());
      if (pv) {
        policyViolationIds.push(pv.id);
      } else {
        notFoundViolations.push(pvName);
      }
    }

    if (notFoundViolations.length > 0) {
      errors[`${i}.policyViolations`] = `Policy violation ${
        notFoundViolations.length > 1 ? 's' : ''
      } not found: ${notFoundViolations.join(', ')}`;
    }

    if (policyViolationIds.length === 0 && policyViolationNames.length > 0) {
      if (!errors[`${i}.policyViolations`]) {
        errors[`${i}.policyViolations`] = 'No valid policy violations found';
      }
    }

    if (!dateOfIncident) {
      errors[`${i}.dateOfIncident`] = 'Date of Incident is required';
    }

    if (!descriptionOfFeedback.trim()) {
      errors[`${i}.descriptionOfFeedback`] = 'Description of Feedback is required';
    }

    if (!expectationForImprovement.trim()) {
      errors[`${i}.expectationForImprovement`] = 'Expectations for Improvement is required';
    }

    if (Object.keys(errors).some((key) => key.startsWith(`${i}.`))) {
      continue;
    }

    const immediateFeedbackValue =
      immediateFeedback.toLowerCase() === 'yes' || immediateFeedback.toLowerCase() === 'y' ? 'yes' : 'no';

    feedbacks.push({
      userId: employee.user.id,
      name: `${employee.user.firstName} ${employee.user.lastName}`,
      dateOfIncident,
      position: employee.title?.name || '',
      department: employee.department?.name || '',
      supervisor: employee.supervisor?.name || '',
      dateOfHire: employee.startDate || '2024-01-01',
      policyViolationIds,
      descriptionOfFeedback,
      immediateFeedback: immediateFeedbackValue,
      expectationForImprovement,
      emailOfFeedback: 'no',
      approved: true,
    });
  }

  return { feedbacks, errors };
};

export const submitPerformanceFeedbackImport = async (
  token: string,
  schoolId: string,
  feedbacks: ProcessedFeedback[],
): Promise<void> => {
  const service = PerformanceFeedbackService(token);
  await service.create({ schoolId, feedbacks, isImport: true });
};

export const getPerformanceFeedbackFieldConfig = (
  value: string,
  name: string,
  employeesList: any[],
  _policyViolationsList: any[],
): JSX.Element | null => {
  if (
    name.split('][')[1].toLocaleLowerCase() === 'staff_name]' ||
    name.split('][')[1].toLocaleLowerCase() === 'staffname]'
  ) {
    const selected = employeesList.find((emp: any) => {
      const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
      return fullName.toLowerCase() === String(value).toLowerCase().trim() || emp.user?.id === value;
    });
    const defaultValue = selected?.user?.id || value || '';
    return (
      <Input type="select" defaultValue={defaultValue} name={name}>
        <option value={''}></option>
        {employeesList.map((emp: any) => {
          const fullName = `${emp.user?.firstName || ''} ${emp.user?.lastName || ''}`.trim();
          return (
            <option key={emp.user?.id} value={emp.user?.id}>
              {fullName}
            </option>
          );
        })}
      </Input>
    );
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'policy_violations]' ||
    name.split('][')[1].toLocaleLowerCase() === 'policyviolations]'
  ) {
    return <Input type="text" defaultValue={value} name={name} placeholder="Separate multiple with comma" />;
  }

  if (
    name.split('][')[1].toLocaleLowerCase() === 'immediate_feedback]' ||
    name.split('][')[1].toLocaleLowerCase() === 'immediatefeedback]'
  ) {
    const options = [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ];
    const selectedValue = String(value).toLowerCase() === 'yes' || String(value).toLowerCase() === 'y' ? 'yes' : 'no';
    return (
      <Input type="select" defaultValue={selectedValue} name={name}>
        {options.map((a: any, index: number) => {
          return (
            <option value={a.value} key={`immediate-feedback-option-${index}`}>
              {a.label}
            </option>
          );
        })}
      </Input>
    );
  }

  return null;
};
